import { Divider, Link, Stack, Typography } from "@mui/material";
import type { GridColDef } from "@mui/x-data-grid-premium";
import qs from "qs";
import { useCallback } from "react";
import { Link as RouterLink, useParams } from "react-router";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import StatusChip from "@entrilia/shared/components/StatusChip";
import DataGrid from "@entrilia/shared/components/grid/DataGrid";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import { nameof } from "@entrilia/shared/utilities/typeHelper";
import adminApi from "../../../../../api/adminApi";
import type {
  WorkflowActivity,
  WorkflowChecklistStatus,
  WorkflowChecklistSummary,
} from "../../../../../api/types/workflowTypes";
import useClientContext from "../../../../../context/useClientContext";
import { pageRoutes } from "../../../../../routes";
import StepsProgress from "../../../../common/StepsProgress";
import UsersAvatarGroup from "../../../../common/UsersAvatarGroup";

const ChecklistStatusTag = ({ status }: { status: WorkflowChecklistStatus }) => {
  switch (status) {
    case "Completed":
      return <StatusChip label="Completed" withDot color={(t) => t.palette.success.main} />;
    case "InProgress":
      return <StatusChip label="Open" withDot color={(t) => t.palette.info.main} />;
    default:
      return null;
  }
};

const getRowId = (row: WorkflowChecklistSummary) => row.objectId;

const ChecklistNameCell = ({ row }: { row: WorkflowChecklistSummary }) => {
  const { clientCode } = useClientContext();
  const { templateId: workflowTemplateId, id: workflowId } = useParams();

  if (!row.objectType || !row.objectId) {
    return <>{row.name}</>;
  }

  const workflowParamsString = qs.stringify({ workflowTemplateId, workflowId });
  const checklistRoute = `/${clientCode}/${pageRoutes.workflows}/${pageRoutes.workflowChecklists}/${row.objectType}/${row.objectId}?${workflowParamsString}`;

  return (
    <Link
      component={RouterLink}
      to={checklistRoute}
      underline="none"
      onClick={(e) => e.stopPropagation()}
      sx={{ color: "text.primary", "&:hover": { color: "primary.main" } }}
    >
      {row.name}
    </Link>
  );
};

const checklistColumns: GridColDef<WorkflowChecklistSummary>[] = [
  {
    field: nameof<WorkflowChecklistSummary>("name"),
    headerName: "Name",
    flex: 1.5,
    minWidth: 220,
    renderCell: ({ row }) => <ChecklistNameCell row={row} />,
  },
  {
    field: nameof<WorkflowChecklistSummary>("status"),
    headerName: "Status",
    flex: 1,
    minWidth: 140,
    renderCell: ({ row }) => <ChecklistStatusTag status={row.status} />,
  },
  {
    field: nameof<WorkflowChecklistSummary>("completedTasksCount"),
    headerName: "Tasks",
    flex: 0.75,
    minWidth: 120,
    renderCell: ({ row }) => <Typography>{`${row.completedTasksCount}/${row.totalTasksCount}`}</Typography>,
  },
  {
    field: nameof<WorkflowChecklistSummary>("approvers"),
    headerName: "Approvers",
    flex: 1,
    minWidth: 140,
    renderCell: ({ row }) => <UsersAvatarGroup users={row.approvers} max={3} />,
  },
];

interface Props {
  workflowId: string;
  workflowActivity: WorkflowActivity;
}

const RunChecklistActivityContent = ({ workflowId, workflowActivity }: Props) => {
  const isFetchDisabled = workflowActivity.state === "None" || !workflowActivity.workflowActivityId;

  const getWorkflowActivityChecklists = useCallback(
    () => adminApi.getWorkflowActivityChecklists(workflowId, workflowActivity.workflowActivityId ?? ""),
    [workflowActivity.workflowActivityId, workflowId]
  );

  const [workflowChecklists, fetchError] = useFetch(getWorkflowActivityChecklists, {
    disabled: isFetchDisabled,
  });

  if (fetchError) {
    logError(fetchError, "[RunChecklistActivityContent] getWorkflowActivityChecklists");
    return <DataLoadingFailed title="Could not load workflow checklists" />;
  }

  if (!isFetchDisabled && workflowChecklists === undefined) {
    return <InlineLoader />;
  }

  const checklists = workflowChecklists ?? [];

  const completedChecklistsCount = checklists.filter((checklist) => checklist.status === "Completed").length;
  const totalChecklistsCount = checklists.length;

  return (
    <>
      <Divider />
      <Stack spacing={1.5} sx={{ pt: 1 }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <Typography variant="subtitle1">Checklists</Typography>
          <StepsProgress completed={completedChecklistsCount} total={totalChecklistsCount} width="10rem" />
        </Stack>

        <DataGrid<WorkflowChecklistSummary>
          rows={checklists}
          columns={checklistColumns}
          getRowId={getRowId}
          noRowsText="No checklists"
          disableColumnPinning
          disableColumnReorder
          disableColumnSorting
          noScrollableContainer
        />
      </Stack>
      <Divider />
    </>
  );
};

export default RunChecklistActivityContent;
